import { useId } from 'react'
import { Link } from '@tanstack/react-router'
import { formatDate } from '~/lib/date'
import { loadPagefind, useNewsSearch } from '~/lib/news/search'
import type { SearchHit } from '~/lib/news/search'
import type { NewsType } from '~/lib/news/types'

/**
 * The search field above the news list. Controlled by the caller, which
 * keeps the query in the URL (`?q=`). Focusing it starts loading the search
 * index, so the first results come quickly.
 */
export function NewsSearchBox({
  value,
  onChange,
  onSubmit,
  className = '',
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  className?: string
}) {
  const id = useId()
  return (
    <form
      role="search"
      className={className}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <label htmlFor={id} className="sr-only">
        Search news and blog posts
      </label>
      <div className="relative">
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-fg-subtle"
        >
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="m13 13 4 4" />
        </svg>
        <input
          id={id}
          type="search"
          name="q"
          value={value}
          autoComplete="off"
          enterKeyHint="search"
          placeholder="Search"
          onFocus={() => void loadPagefind().catch(() => {})}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value) {
              e.preventDefault()
              onChange('')
            }
          }}
          className="block h-12 w-full rounded-full border border-line bg-surface pr-4 pl-10 text-base leading-6 text-fg placeholder:text-fg-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </div>
    </form>
  )
}

/** Results for `query` in the current tab; replaces the paginated list. */
export function NewsSearchResults({
  query,
  tab,
  tabLabel,
}: {
  query: string
  tab: NewsType
  tabLabel: string
}) {
  const { status, hits, total, more, showMore } = useNewsSearch(query, tab)
  const where = tab === 'all' ? '' : ` in ${tabLabel}`
  return (
    <div>
      <p role="status" aria-live="polite" className="pb-4 text-fg-muted">
        {status === 'loading' && 'Searching…'}
        {status === 'ready' &&
          (total === 0
            ? `No results for “${query}”${where}.`
            : `${total.toLocaleString('en-US')} ${total === 1 ? 'result' : 'results'} for “${query}”${where}`)}
        {status === 'error' &&
          'Search isn’t available right now. Please try again later.'}
      </p>
      <ol
        aria-busy={status === 'loading'}
        className={`flex flex-col gap-8 transition-opacity ${status === 'loading' ? 'opacity-50' : ''}`}
      >
        {hits.map((hit) => (
          <li key={hit.id}>
            <Hit hit={hit} />
          </li>
        ))}
      </ol>
      {status === 'ready' && more && (
        <div className="pt-8">
          <button
            type="button"
            onClick={showMore}
            className="cursor-pointer rounded-full bg-surface-alt px-6 py-3 font-semibold text-fg hover:bg-surface-alt-hover"
          >
            Show more results
          </button>
        </div>
      )}
    </div>
  )
}

function Hit({ hit }: { hit: SearchHit }) {
  const { title, label, outlet, date } = hit.meta
  const meta = [label, outlet, date && formatDate(date)]
    .filter(Boolean)
    .join(' · ')
  return (
    <article>
      {meta && (
        <p className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
          {meta}
        </p>
      )}
      <h3 className="text-xl leading-7 font-bold">
        {/* Pagefind gives `/x/`; the canonical URL has no trailing slash. */}
        <Link
          to={hit.url.replace(/\/$/, '')}
          className="text-fg hover:text-accent hover:underline"
        >
          {title ?? hit.url}
        </Link>
      </h3>
      <p
        className="pt-1 text-fg-body [&_mark]:rounded-sm [&_mark]:bg-accent/20 [&_mark]:text-fg"
        dangerouslySetInnerHTML={{ __html: hit.excerpt }}
      />
    </article>
  )
}
